import { useLocation } from "react-router";
import { DashboardLayout } from "@/pages/Dashboard";
import { formatDate } from "@/utils/helpers";

type LegacyPlanQuestion = {
  question?: unknown;
  answer?: unknown;
};

type IcdQuestionGroup = {
  questions?: LegacyPlanQuestion[];
};

type LegacyPlanViewState = {
  legacyPlan?: Record<string, unknown>;
  legacyPlanType?: "care-plan" | "treatment-plan";
};

function getFirstValue(record?: Record<string, unknown>, keys: string[] = []) {
  if (!record) return "";

  for (const key of keys ?? []) {
    const value = record?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }

  return "";
}

function formatListValue(value: unknown) {
  const formatted = Array.isArray(value) ? value.join(", ") : value || "";
  return String(formatted).replace(/~/g, ", ");
}

function formatAnswer(value: unknown) {
  if (Array.isArray(value)) return value.filter(Boolean).join(", ") || "-";
  if (value && typeof value === "object") return JSON.stringify(value);
  return String(value || "-");
}

function flattenIcdQuestions(groups: unknown) {
  if (!Array.isArray(groups)) return [];

  return groups?.flatMap((group) => {
    const typedGroup = group as IcdQuestionGroup;
    return Array.isArray(typedGroup?.questions) ? typedGroup.questions : [];
  }) ?? [];
}

function getPlanQuestions(plan: Record<string, unknown> | undefined) {
  if (!plan) return [];

  if (Array.isArray(plan?.questions) && plan?.questions?.length > 0) {
    return plan?.questions as LegacyPlanQuestion[];
  }

  if (Array.isArray(plan?.generalQuestions) && plan?.generalQuestions?.length > 0) {
    return plan?.generalQuestions as LegacyPlanQuestion[];
  }

  return flattenIcdQuestions(plan?.icdQuestionGroups);
}

export default function LegacyPlanView() {
  const location = useLocation();
  const state = (location?.state || {}) as LegacyPlanViewState;
  const plan = state?.legacyPlan;
  const planType = state?.legacyPlanType || (
    location?.pathname?.includes("treatment-plan") ? "treatment-plan" : "care-plan"
  );
  const title = planType === "treatment-plan" ? "Treatment Plan" : "Care Plan";
  const questions = getPlanQuestions(plan);

  const patientName = String(getFirstValue(plan, ["patientName", "name"]) || "-");
  const dateOfBirth = getFirstValue(plan, ["dateOfBirth", "dob", "dateOfBirthLocal"]);
  const creationDate = getFirstValue(plan, [
    "dateOfCreation",
    "createdAt",
    "dateOfCreationLocal",
    "createdAtLocal",
  ]);
  const icdCodes = formatListValue(getFirstValue(plan, ["icdCodes", "icdCode"]));
  const program = String(getFirstValue(plan, ["program", "planType"]) || "-");

  return (
    <DashboardLayout
      activePage={
        planType === "treatment-plan"
          ? "view-legacy-treatment-plan"
          : "view-legacy-careplan"
      }
    >
      <div className="min-h-[calc(100vh-80px)] bg-[#F5F5F5] px-4 py-8">
        <div className="mx-auto w-full max-w-[620px] bg-white px-10 py-8 shadow-sm">
          <div>
            {/* <h1 className="mb-5 text-lg font-semibold text-[#111827]">{title}</h1> */}

            {!plan ? (
              <div className="rounded-lg border border-dashed border-[#D1D5DB] bg-[#FAFAFA] p-6 text-sm text-muted-foreground">
                No plan data found. Please open this page from the past medical records {title.toLowerCase()} list.
              </div>
            ) : (
              <>
                <div className="bg-grey-light p-5 rounded-md">
                  <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
                    <div>
                      <span className="teachers text-sm font-semibold uppercase text-tertiary">
                        Patient Name:
                      </span>
                      <span className="teachers ml-2 text-sm text-black">{patientName}</span>
                    </div>
                    <div>
                      <span className="teachers text-sm font-semibold uppercase text-tertiary">
                        Date of Birth:
                      </span>
                      <span className="teachers ml-2 text-sm text-black">{formatDate(dateOfBirth as string)}</span>
                    </div>
                    <div>
                      <span className="teachers text-sm font-semibold uppercase text-tertiary">
                        Program:
                      </span>
                      <span className="teachers ml-2 text-sm text-black">{program}</span>
                    </div>
                    <div>
                      <span className="teachers text-sm font-semibold text-tertiary">
                        Creation Date:
                      </span>
                      <span className="teachers ml-2 text-sm text-black">
                        {formatDate(creationDate as string)}
                      </span>
                    </div>
                  </div>

                  {icdCodes ? (
                    <div className="mt-4 border-t border-[#DDDDDD] pt-4">
                      <span className="text-sm font-semibold uppercase text-tertiary">
                        ICD Codes:
                      </span>
                      <span className="ml-2 text-sm text-black">{icdCodes}</span>
                    </div>
                  ) : null}

                </div>

                <div className="mt-6">
                  {questions?.length > 0 ? (
                    questions?.map((item, index) => (
                      <div
                        key={`${String(item?.question || "question")}-${index}`}
                        className="border-b border-[#DDDDDD] py-4 last:border-b-0"
                      >
                        <p className="teachers text-[12px] font-medium text-[#07041B]">
                          {String(item?.question || `Question ${index + 1}`)}
                        </p>
                        <p className="teachers mt-2 whitespace-normal wrap-break-word text-[12px] font-semibold leading-relaxed text-[#07041B]">
                          {formatAnswer(item?.answer)}
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-lg border border-dashed border-[#D1D5DB] bg-[#FAFAFA] p-6 text-sm text-muted-foreground">
                      No questions found for this {title?.toLowerCase()}.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
